import { useState, useEffect, useRef, useCallback } from 'react';
import WebflowPublishNotice from '../components/WebflowPublishNotice';
import { startWebflowConnect } from '../utils/webflowConnect';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Atom } from "react-loading-indicators";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowLeft, faCircleRight, faCheck, faCircleCheck, faXmark, faPenToSquare, faSpinner, faX, faPlus } from '@fortawesome/free-solid-svg-icons';
import BotChatStep from '../components/BotChatStep';
import BackButton from '../components/BackButton';
import { saveInteraction } from "../components/Interaction";
import './LandingPageRegister.css';

// The paid step ("choose a subscription") and the "your assistant is live"
// slide it forwarded to are HIDDEN, not removed: the flow now ends at Test with
// a handoff into the portal. priceMap, checkout and plan polling are left
// intact below — flip this back to true to put both slides back in the wizard.
// The Stripe-return handler (?session_id= -> a session token from
// stripe/validate-session) is gone (Webflow audit 30.10.2026): a token must
// never come back through a URL parameter; plan polling covers the return.
const SHOW_SUBSCRIPTION_STEP = false;

// The Test step (chat with the new agent) is left out for now: a Webflow agent
// starts without knowledge, so the test chat has nothing to answer from.
// Flip to true to put it back between Design and the add step.
const SHOW_TEST_STEP = false;

function BillingCycleToggle({ billingCycle, setBillingCycle, t }) {
  return (
    <div className="billing-toggle" data-cycle={billingCycle}>
      <span className="billing-thumb" aria-hidden />
      <button
        type="button"
        className={billingCycle === "MONTH" ? "active" : ""}
        onClick={() => setBillingCycle("MONTH")}
      >
        {t("monthly")}
      </button>
      <button
        type="button"
        className={billingCycle === "YEAR" ? "active" : ""}
        onClick={() => setBillingCycle("YEAR")}
      >
        {t("yearly")}
      </button>
    </div>
  );
}

function PriceSwap({ showYearly, yearly, monthly, reducedMotion = false }) {
  const prefersReduced = typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const noAnim = reducedMotion || prefersReduced;

  return (
    <div className={`price-swap ${noAnim ? "no-anim" : ""}`}>
      <div className={`pane ${showYearly ? "show" : ""}`} aria-hidden={!showYearly}>
        {yearly}
      </div>
      <div className={`pane ${!showYearly ? "show" : ""}`} aria-hidden={showYearly}>
        {monthly}
      </div>
    </div>
  );
}

// One OAuth callback per page load (Webflow Marketplace #1181064). React can
// mount this page more than once in the same load (a parent re-render behind
// a spinner, StrictMode in development). A second mount finds the address bar
// already stripped, so it must neither lose the callback nor bounce the
// customer back to the Webflow authorization screen. The code is never kept:
// it goes straight into the single check_account request, and only that
// request's pending/settled promise is remembered here.
let callbackExchange = null;

// Nothing to resume (the 30 minutes are over, another browser, the address
// opened directly): the error screen, whose button starts the authorization.
// A reload never starts it by itself (guideline: no OAuth on page refresh).
function resumeAfterReload() {
  const nothingToResume = () => ({ ok: false, status: 0, j: { detail: 'nothing_to_resume' } });
  return fetch(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/resume`, { method: 'POST' })
    .then(async (r) => {
      const j = r.ok ? await r.json().catch(() => ({})) : {};
      if (!r.ok || !j.registration_id) return nothingToResume();
      return { ok: true, status: r.status, j };
    })
    .catch(nothingToResume);
}

function exchangeCallback(code, state) {
  // code + state go in the POST body, never in a query string. The backend
  // verifies the state nonce it minted and tells us which flow this is; the
  // Webflow token stays on the server.
  return fetch(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/check_account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state: state || null }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, j: await r.json().catch(() => ({})) }));
}

// A callback must carry the state we minted (Webflow guideline "OAuth flow
// security 1"); the backend never exchanges a code without one. A stateless
// callback (an install started somewhere other than our install URL) starts
// the authorization again, once, through that URL. A second one within two
// minutes gets the error screen instead of a loop.
const STATE_RESTART_KEY = 'ub_wf_state_restart';
const STATE_RESTART_WINDOW_MS = 120000;

function restartWithStateOnce() {
  try {
    const last = Number(sessionStorage.getItem(STATE_RESTART_KEY) || 0);
    if (Date.now() - last < STATE_RESTART_WINDOW_MS) {
      sessionStorage.removeItem(STATE_RESTART_KEY);
      return false;
    }
    sessionStorage.setItem(STATE_RESTART_KEY, String(Date.now()));
  } catch {
    // Without storage a loop cannot be told apart: the error screen, whose
    // button starts the authorization deliberately.
    return false;
  }
  window.location.replace(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/connect`);
  return true;
}

// The build's progress (Webflow Marketplace #1197336): the agent's own status,
// polled, is what moves the page on; the WebSocket only makes it quicker. A
// socket that subscribes after the build already finished (Redis pub/sub keeps
// nothing for late subscribers) can therefore no longer leave the page waiting.
const PROGRESS_POLL_MS = 2000;
// After this long the page offers the dashboard; the build keeps running there.
const PROGRESS_SLOW_MS = 90000;

export default function WebflowRegister ({ onSetupComplete, onConnected }) {
  const completedRef = useRef(false);
  const botRef = useRef('');
  const activatedRef = useRef(false);
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [err, setErr] = useState('');
  // Set when the callback cannot continue (authorization declined, or the
  // exchange was refused): a dedicated screen with a deliberate way back into
  // OAuth instead of an automatic redirect.
  const [callbackError, setCallbackError] = useState('');
  // Opaque id of the registration the backend parked for the password step.
  // The Webflow access token itself never reaches the browser.
  const [registrationId, setRegistrationId] = useState('');
  const [needsPublish, setNeedsPublish] = useState(false);
  const [addedToSite, setAddedToSite] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(false);
  // The add step keeps its "Published" confirmation instead of hiding it.
  const [publishedSite, setPublishedSite] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  // Existing account without a known password (#1197336): a code mailed to it.
  const [codeSent, setCodeSent] = useState(false);
  const [signinCode, setSigninCode] = useState('');
  const [notice, setNotice] = useState('');
  const [slowBuild, setSlowBuild] = useState(false);
  const [urls, setUrls] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState('');
  const [siteId, setSiteId] = useState('');
  const [pct, setPct] = useState(0);
  const [bot, setBot] = useState("");
  const [uid, setUid] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [token, setToken] = useState("");
  const [isTraining, setIsTraining] = useState(false);
  const [firstName, setFirstName] = useState('');
  const planWrapperRef = useRef(null);
  const planCardRefs = useRef([]);
  const plans = ["smart", "boost", "ultimo"];
  const priceMap = {
    MONTH: {
      smart: { priceId: "price_1RhFgODR0WHHWTu7d1OFpnRj", planId: 5, labelKey: "wf_plan_smart_price_monthly" },
      boost: { priceId: "price_1RhFgfDR0WHHWTu7XcncK6g6", planId: 6, labelKey: "wf_plan_boost_price_monthly" },
      ultimo: { priceId: "price_1RhFhADR0WHHWTu7rgs5eOCQ", planId: 7, labelKey: "wf_plan_ultimo_price_monthly" },
    },
    YEAR: {
      smart: {
        savePct: "wf_save_pct_smart",
        priceId: "price_1Ssr20DR0WHHWTu7jhau0DTG",
        planId: 5,
        labelKey: "wf_plan_smart_price_yearly",
        yearlyOriginalKey: "wf_plan_smart_price_yearly",
        yearlyDiscountedKey: "wf_plan_smart_yearly_discounted",
      },
      boost: {
        savePct: "wf_save_pct_boost",
        priceId: "price_1Ssr2JDR0WHHWTu7CkvYQD4m",
        planId: 6,
        labelKey: "wf_plan_boost_price_yearly",
        yearlyOriginalKey: "wf_plan_boost_price_yearly",
        yearlyDiscountedKey: "wf_plan_boost_yearly_discounted",
      },
      ultimo: {
        savePct: "wf_save_pct_ultimo",
        priceId: "price_1T2axQDR0WHHWTu7FEdaXsZk",
        planId: 7,
        labelKey: "wf_plan_ultimo_price_yearly",
        yearlyOriginalKey: "wf_plan_ultimo_price_yearly",
        yearlyDiscountedKey: "wf_plan_ultimo_yearly_discounted",
      },
    },
  };
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('register');
  const [activePlanIdx, setActivePlanIdx] = useState(1);
  const [billingCycle, setBillingCycle] = useState("YEAR");
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState("");
  const planPollRef = useRef(null);
  const defaultWidgetStyles = {
    themeColor: "#000000ff",
    headerColor: "#ffffff",
    hoverColor: "#232323ff",
    pulsing: true,
    widgetHorizontalAlignment: "right",
    widgetSize: 60,
    widgetBorderRadius: 50,
  };

  const [widgetStyles, setWidgetStyles] = useState(defaultWidgetStyles);
  const [noContentFound, setNoContentFound] = useState(false);
  // Slide order: account, create, design, [test], add to site, [plans, success].
  const withTest = SHOW_TEST_STEP && !noContentFound;
  const addStepIndex = withTest ? 4 : 3;

  useEffect(() => {
    // public/index.html already moved the callback parameters out of the URL
    // before anything loaded; read them once from there (fallback: the URL,
    // e.g. under the dev server).
    const handed = window.__UB_WF_CALLBACK__ || null;
    try { delete window.__UB_WF_CALLBACK__; } catch { window.__UB_WF_CALLBACK__ = undefined; }
    const urlParams = new URLSearchParams(window.location.search);
    const rawCode = handed ? handed.code : urlParams.get('code');
    const rawState = handed ? handed.state : urlParams.get('state');
    const rawError = handed ? handed.error : urlParams.get('error');

    // Take ?code and ?state out of the address bar IMMEDIATELY, before any
    // other network activity: the URL must never be observable by analytics,
    // error logging or the browser history once this page is running
    // (Webflow Marketplace #1180349). The code is handed straight to the one
    // exchange request and never enters client state.
    if (rawCode || rawState || rawError) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch { /* non-fatal */ }
    }

    if (rawCode && !rawState) {
      if (restartWithStateOnce()) {
        // A remount while the browser leaves waits on the spinner.
        callbackExchange = new Promise(() => {});
        return;
      }
      callbackExchange = Promise.resolve({ ok: false, status: 0, j: { detail: 'state_required' } });
    } else if (rawCode) {
      try { sessionStorage.removeItem(STATE_RESTART_KEY); } catch { /* non-fatal */ }
      callbackExchange = exchangeCallback(rawCode, rawState);
    } else if (rawError) {
      // The customer declined on the Webflow consent screen (?error=
      // access_denied). Sending them straight back to that screen would be
      // a loop; show where they are and let them choose to authorize again.
      callbackExchange = Promise.resolve({ denied: true });
    } else if (!callbackExchange) {
      // This page is only a valid experience AFTER Webflow authorization —
      // the App Market install redirects here with ?code=. A remount after
      // this load already received its callback never lands here.
      if (localStorage.getItem('token')) {
        // Signed in (e.g. a refresh after the exchange, or the redirect URI
        // opened again): the account exists, so go to the dashboard instead
        // of re-running consent and the wizard (Session behavior: no repeated
        // OAuth on refresh).
        if (onConnected) onConnected('/overview');
        else navigate('/overview', { replace: true });
        return;
      }
      // A reload at the account step resumes the registration this browser
      // already has (#1181064: OAuth completes once); with nothing to resume
      // the visitor gets the error screen and its button instead of an
      // unauthorized wizard.
      callbackExchange = resumeAfterReload();
    }

    let alive = true;
    (async () => {
      try {
        setPageLoading(true);
        const { ok, status, j = {}, denied } = await callbackExchange;
        if (!alive) return;

        if (denied) {
          setCallbackError(t('wf_oauth_denied'));
          return;
        }

        // "Connect existing bot" flow (started from the dashboard): the backend
        // answers with status connected / connected_and_activated. The customer
        // is already signed in; hand the session to the app shell before
        // leaving the callback so the protected route does not bounce to /login.
        if (ok && (j.status === 'connected' || j.status === 'connected_and_activated')) {
          const rt = typeof j.return_to === 'string' && j.return_to.startsWith('/agents/new/') ? j.return_to : null;
          const activated = j.status === 'connected_and_activated';
          const to = rt || '/integration';
          const navState = rt
            ? { webflow: true, activated, needsPublish: !!j.needs_publish }
            : (activated ? { tab: 'webflow', activated: true, needsPublish: !!j.needs_publish } : { tab: 'webflow' });
          callbackExchange = null;
          if (onConnected) onConnected(to, navState);
          else navigate(to, { replace: true, state: navState });
          return;
        }
        if (!ok) {
          // Expired/used state, refused code: start the flow again.
          console.error('Webflow check_account failed:', status, j.detail);
          setCallbackError(t('wf_oauth_restart'));
          return;
        }

        // An existing portal account (same e-mail as the Webflow account)
        // signs in with its password; the e-mail match alone never signs in.
        // Said plainly, with the way on for a customer who does not know that
        // account's password: a code to its address (#1197336).
        if (j.has_account) {
          setAuthMode('login');
          setNotice(t('wf_account_exists_code', { email: j.user_email }));
        }
        setRegistrationId(j.registration_id || '');
        setUserEmail(j.user_email);
        saveInteraction({ action: 'webflow_page_loaded' });

        /* j.urls is now [{ url, site_id }, ...] */
        const urlsArr = Array.isArray(j.urls) ? j.urls : [];
        setUrls(urlsArr);
        if (urlsArr.length) {
          setSelectedUrl(urlsArr[0].url);
          setSiteId(urlsArr[0].site_id);
        }
      }
      catch(e) {
         console.error(e);
         // Network failure of the exchange: the code may or may not have been
         // consumed, so the only safe path is a fresh authorization.
         if (alive) setCallbackError(t('wf_oauth_restart'));
      }
      finally {
         if (alive) setPageLoading(false);
      }
    })();

    return () => { alive = false; };
    // Mount-only: the callback is read from the URL exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { botRef.current = bot || ''; }, [bot]);

  // Leave the progress screen exactly once, whichever signal comes first.
  const finishBuild = useCallback(async (noContent) => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (noContent) setNoContentFound(true);
    await fetchWidgetConfiguration(botRef.current);
    setStep(2);
    setIsTraining(false);
    setSlowBuild(false);
  // fetchWidgetConfiguration is stable (useCallback with no deps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const failBuild = useCallback(() => {
    setErr(t("onboard_bot_creation_failed"));
    setIsTraining(false);
    setPct(0);
    setBot(null);
  }, [t]);

  // The WebSocket only speeds things up. It is opened once per signed-in page
  // (not again when the agent id arrives) and reads the current agent from a ref.
  useEffect(() => {
    if (!uid) return undefined;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) return undefined;
      ws = new WebSocket(raw.replace(/^http/, "ws"));
    } catch (err) {
      console.error("WebSocket init failed:", err);
      return undefined;
    }
    ws.addEventListener("open", () => {
      ws.send(JSON.stringify({ type: "init", user_id: uid, token: localStorage.getItem('token') }));
    });
    ws.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        const current = botRef.current;
        if (!current || (msg.bot_id && msg.bot_id !== current)) return;
        if (msg.percentage != null) setPct((p) => Math.max(p, msg.percentage));
        if (msg.status === "completed") finishBuild(false);
        if (msg.status === "no_content_found") finishBuild(true);
        if (msg.status === "failed") failBuild();
      } catch (e) {
        console.error("WebSocket message parse error", e);
      }
    });
    return () => ws.close();
  }, [uid, finishBuild, failBuild]);

  // The agent's own status decides: polled every 2 s while it is being built,
  // with or without the socket. After 90 s the page offers the dashboard.
  useEffect(() => {
    if (!bot) return undefined;
    let alive = true;
    const started = Date.now();
    async function pollOnce() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${encodeURIComponent(bot)}`);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        if (data.creation_percentage != null) setPct((p) => Math.max(p, data.creation_percentage));
        if (data.status === "no_content_found") finishBuild(true);
        else if (data.status === "failed") failBuild();
        else if (data.status === "completed" || data.creation_percentage >= 100) finishBuild(false);
      } catch (e) {
        // A missed poll is retried on the next tick; the time limit below
        // still gives the customer a way on.
        console.error("Polling error:", e);
      }
      if (alive && !completedRef.current && Date.now() - started > PROGRESS_SLOW_MS) setSlowBuild(true);
    }
    pollOnce();
    const timerId = setInterval(() => {
      if (completedRef.current) { clearInterval(timerId); return; }
      pollOnce();
    }, PROGRESS_POLL_MS);
    return () => { alive = false; clearInterval(timerId); };
  }, [bot, finishBuild, failBuild]);

  useEffect(() => {
    const gclid = new URLSearchParams(window.location.search).get('gclid');
    if (gclid) localStorage.setItem('gclid', gclid);
  }, []);

  useEffect(() => {
    if (step !== addStepIndex + 1) return;
    const wrapper = planWrapperRef.current;
    const cards   = planCardRefs.current;
    if (!wrapper || cards.length === 0) return;
    const GAP = 20;
    const updateActiveIdx = () => {
      const cardWidth = cards[0].offsetWidth;
      const idx = Math.round(wrapper.scrollLeft / (cardWidth + GAP));
      setActivePlanIdx(Math.min(cards.length - 1, Math.max(0, idx)));
    };

    updateActiveIdx();
    wrapper.addEventListener("scroll", updateActiveIdx, { passive: true });

    return () => wrapper.removeEventListener("scroll", updateActiveIdx);
  }, [step, addStepIndex]);

  useEffect(() => () => stopPlanPolling(), []);
  useEffect(() => { if (step !== addStepIndex + 1) stopPlanPolling(); }, [step, addStepIndex]);

  const fetchWidgetConfiguration = useCallback(
    async (botId) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${botId}`
        );
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();

        const fetchedStyles = {
          themeColor: data.theme_color,
          hoverColor: data.button_hover_color,
          headerColor: data.header_font_color,
          pulsing: data.pulsing,
          widgetHorizontalAlignment: data.widget_horizontal_alignment,
          widgetBorderRadius: data.widget_border_radius ?? 50,
          widgetSize: data.widget_size ?? 70
        };

        setWidgetStyles(fetchedStyles);
      } catch (err) {
        console.error("Error fetching widget configuration:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Put the widget on the chosen Webflow site. Only the "Add to my site"
  // button calls this (Webflow #1197336: adding the agent to the site is its
  // own, deliberate step, never a side effect of another click). Idempotent on
  // the backend and ref-guarded here. It registers and applies the script
  // only; the customer publishes the site themselves (Publish button below,
  // or in the Webflow Designer).
  const ensureWidgetActivated = useCallback(async () => {
    if (activatedRef.current || !bot || !siteId) return;
    activatedRef.current = true;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/activate_chatbot?bot_id=${encodeURIComponent(bot)}&site_id=${encodeURIComponent(siteId)}`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.status === 'token_revoked') setErr(t('wf_token_revoked'));
        throw new Error(`activate_chatbot returned ${res.status}`);
      }
      setNeedsPublish(true);
      setAddedToSite(true);
      saveInteraction({ action: 'webflow_widget_activated' });
    } catch (e) {
      // Never block onboarding on this: the button can be clicked again, and
      // the owner still has Deploy → Channels → Webflow.
      activatedRef.current = false;
      setAddError(true);
      console.error('Webflow widget activation failed:', e);
    }
  }, [bot, siteId, t]);

  const addToSite = async () => {
    setAddError(false);
    setAdding(true);
    try { await ensureWidgetActivated(); } finally { setAdding(false); }
  };

  // The site as the customer knows it, without scheme or trailing slash.
  const siteHost = (selectedUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

  // Activation is a state change on the customer's Webflow site, so it runs
  // only from the "Add to my site" click (Webflow guideline "User
  // interaction 3"; #1197336): never from another button, a timer or an
  // effect after the build.

  async function handleLogin(e) {
    e.preventDefault();
    setErr('');
    try {
      setLoading(true);
      // Signing in here also attaches this install's Webflow authorization
      // (parked server-side under the registration id) to that account.
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(codeSent
          ? { registration_id: registrationId, email: userEmail, code: signinCode.trim() }
          : { registration_id: registrationId, email: userEmail, password: pw }),
      });

      if (response.ok) {
        const data = await response.json();
        setUid(data.user_id);
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user_id', data.user_id);
        if (data.first_name) localStorage.setItem('first_name', data.first_name);
        const urlsArr = Array.isArray(data.urls) ? data.urls : [];
        if (urlsArr.length) {
          setUrls(urlsArr);
          setSelectedUrl(urlsArr[0].url);
          setSiteId(urlsArr[0].site_id);
        }
        setStep(1);
      } else if (response.status === 400) {
        setCallbackError(t('wf_oauth_restart'));
      } else {
        setErr(codeSent ? t('wf_code_invalid') : t('invalid_credentials'));
      }
    } catch (err) {
      setErr(t('login_error'));
    } finally {
      setLoading(false);
    }
  }

  // No password at hand: a 6-digit code to the account's address signs in and
  // the install continues right here.
  async function sendCode() {
    setErr('');
    setNotice('');
    const email = (userEmail || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr(t('email_invalid') || 'Please enter a valid email address.');
      return;
    }
    try {
      setLoading(true);
      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/login_code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, email }),
      });
      if (r.ok) {
        setCodeSent(true);
        setSigninCode('');
        setPw('');
        setNotice(t('wf_code_sent', { email }));
      } else if (r.status === 404) {
        setErr(t('wf_no_account_for_email', { email }));
      } else if (r.status === 429) {
        setErr(t('wf_too_many_codes'));
      } else if (r.status === 400) {
        const body = await r.json().catch(() => ({}));
        if (/expired/i.test(body.detail || '')) setCallbackError(t('wf_oauth_restart'));
        else setErr(t('wf_code_send_failed'));
      } else {
        setErr(t('wf_code_send_failed'));
      }
    } catch {
      setErr(t('wf_code_send_failed'));
    } finally {
      setLoading(false);
    }
  }

  function switchAuthMode(mode, { keepEmail = false } = {}) {
    setErr('');
    setNotice('');
    setPw('');
    setPw2('');
    setSigninCode('');
    setCodeSent(false);
    if (!keepEmail) setUserEmail('');
    setAuthMode(mode);
  }

  async function handlePw (e){
    e.preventDefault();
    setErr('');
    const email = (userEmail || '').trim();
    const first = (firstName || '').trim();
    const pass1 = pw || '';
    const pass2 = pw2 || '';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setErr(t('email_required') || 'Please enter your email.');
      return;
    }
    if (!emailRegex.test(email)) {
      setErr(t('email_invalid') || 'Please enter a valid email address.');
      return;
    }
    if (!first) {
      setErr(t('first_name_required') || 'Please enter your first name.');
      return;
    }
    if (pass1.length < 8) {
      setErr(t('password_too_short') || 'Password must be at least 8 characters long.');
      return;
    }
    if (pass1 !== pass2) {
      setErr(t('passwords_do_not_match') || 'Passwords do not match.');
      return;
    }

    try{
      setLoading(true);
      // Password, e-mail and registration id travel in the body; the Webflow
      // token stays in the server-side registration. The account takes the
      // e-mail typed here (prefilled with the Webflow account's).
      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, email, password: pw, first_name: firstName }),
      });
      if (r.status === 409) {
        const body = await r.json().catch(() => ({}));
        switchAuthMode('login', { keepEmail: true });
        if (body.email) setUserEmail(body.email);
        setNotice(t('wf_account_exists_code', { email: body.email || email }));
        return;
      }
      if (r.status === 400) {
        const body = await r.json().catch(() => ({}));
        if (/expired/i.test(body.detail || '')) { setCallbackError(t('wf_oauth_restart')); return; }
        if (body.detail === 'invalid_email') { setErr(t('email_invalid')); return; }
        throw new Error();
      }
      if(!r.ok) throw new Error();
      const j = await r.json();
      saveInteraction({ action: 'webflow_account_created' });

      /* j.urls is now [{ url, site_id }, ...] */
      const urlsArr = Array.isArray(j.urls) ? j.urls : [];
      setUrls(urlsArr);
      if (urlsArr.length) {
        setSelectedUrl(urlsArr[0].url);
        setSiteId(urlsArr[0].site_id);
      }

      setUid(j.user_id);
      setToken(j.token);
      localStorage.setItem('token', j.token);
      localStorage.setItem('user_id', j.user_id);
      localStorage.setItem('first_name', firstName);
      setStep(1);
    }
    catch {
      setErr(t('set_password_error'));
    }
    finally {
      setLoading(false);
    }
  }

  function extractDomainName(inputUrl) {
    try {
      const prefixedUrl = inputUrl.startsWith('http') ? inputUrl : 'https://' + inputUrl;
      const parsedUrl = new URL(prefixedUrl);

      let host = parsedUrl.hostname;
      if (host.startsWith('www.')) host = host.slice(4);
      const parts = host.split('.');

      if (parts.length >= 2) {
        return parts[parts.length - 2];
      }

      return host;
    } catch {
      return 'bot';
    }
  }

  // Webflow returned no site for this authorization (none selected on the
  // consent screen): authorize again and pick one, instead of a dead end.
  async function chooseSiteAgain() {
    try {
      await startWebflowConnect({ authorize: true });
    } catch (e) {
      console.error('Webflow re-authorization failed:', e);
      setErr(t('wf_oauth_restart'));
    }
  }

  async function createBot() {
    setErr('');

    if (!selectedUrl || !siteId) {
      setErr(urls.length ? t('onboard_select_site_url') : t('wf_no_sites_text'));
      return;
    }

    // The site only names the agent and is where it gets installed. The
    // Webflow site is never crawled (Webflow Marketplace): the agent learns
    // from files and Q&A the owner adds in the dashboard. No URL is sent and
    // the agent is flagged Webflow-hosted, which also keeps every
    // website-crawl surface off for it (server-side gate + webflowGate.js).
    const fd = new FormData();
    fd.append('bot_name', extractDomainName(selectedUrl));
    fd.append('language', 'English');
    fd.append('webflow_hosted', true);
    // The chosen site: the agent is bound to it from creation on, so it
    // answers no other origin even before "Add to my site".
    fd.append('webflow_site_id', siteId);
    fd.append('send_email_to_user', false);
    fd.append('javascript_rendering', false);
    // NOTE deliberately NO wix_code here: on this page `code` was the Webflow
    // OAuth authorization code (single-use, already exchanged and cleared);
    // it must never ride along into an unrelated request (#1180349). The
    // backend does not read wix_code on this path.

    try {
      setIsTraining(true);
      setLoading(true);
      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}create_chatbot`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) throw new Error();
      const j = await r.json();
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'bot_creation_started' });
      }
      setBot(j.bot_id);
      saveInteraction({ action: 'webflow_bot_creation_started' });
    } catch {
      setErr(t('onboard_create_failed_short'));
      setIsTraining(false);
    } finally {
      setLoading(false);
    }
  }

  const handlePortalForward = async (confirmed, token, uid) => {
    // The install is finished: a later in-app visit to this route starts a
    // fresh authorization instead of replaying this load's callback.
    callbackExchange = null;
    onSetupComplete(confirmed, token, uid);
    navigate("/");
  };

  const handleStyleChange = (key, value) => {
    setWidgetStyles((prevStyles) => ({ ...prevStyles, [key]: value }));
  };

  async function saveWidgetStyles() {
    setErr('');
    setLoading(true);

    try {
      // Step 1: Update widget configuration
      const updates = {
        theme_color: widgetStyles.themeColor,
        header_font_color: widgetStyles.headerColor,
        button_hover_color: widgetStyles.hoverColor,
        pulsing: widgetStyles.pulsing,
        widget_horizontal_alignment: widgetStyles.widgetHorizontalAlignment,
        widget_size: widgetStyles.widgetSize,
        widget_border_radius: widgetStyles.widgetBorderRadius,
      };

      const configResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${encodeURIComponent(bot)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        }
      );

      if (!configResponse.ok) {
        throw new Error('Widget configuration failed');
      }

      // Step 2: Proceed to next step: Test, or the add step when there is no
      // knowledge to test.
      saveInteraction({ action: 'webflow_styles_saved' });
      setStep(3);
    } catch (e) {
      setErr(t('onboard_integration_failed'));
      setStep(2);
    } finally {
      setLoading(false);
    }
  }

  const createCheckOutSession = async (priceId, subscriptionPlanId) => {
    try {
      setLoading(true);
      setErr(null);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bot_id: bot,
          price_id: priceId,
          subscription_plan_id: subscriptionPlanId,
          success_url: "https://portal.ultimo-bots.com/checkout-success",
          site_id: siteId
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Checkout session creation failed: ${errorText}`);
      }

      saveInteraction({ action: 'webflow_checkout_session_started' });

      const data = await res.json();

      // Open checkout in new tab instead of redirecting
      window.open(data.checkout_url, "_blank", "noopener,noreferrer");

      // Start polling for plan activation in the current tab
      startPlanPolling();

    } catch (err) {
      console.error("Failed:", err);
      setErr(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanClick = (planKey) => {
    const plan = priceMap[billingCycle][planKey];
    createCheckOutSession(plan.priceId, plan.planId);
  };

  async function fetchBotPlanName(currentBotId = bot) {
    if (!currentBotId) throw new Error("Missing bot id");
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${encodeURIComponent(currentBotId)}&include_plan=true`
    );
    if (!res.ok) throw new Error(`bot fetch failed: ${res.status}`);
    const data = await res.json();
    return data?.subscription_plan?.name ?? "Free";
  }

  function stopPlanPolling() {
    if (planPollRef.current) {
      clearInterval(planPollRef.current);
      planPollRef.current = null;
    }
    setIsCheckingPlan(false);
  }

  function startPlanPolling() {
    if (planPollRef.current || !bot) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);

    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName(bot);
        if (name && name !== "Free") {
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'free_trial_started' });
          }
          saveInteraction({ action: 'webflow_payment_confirmed' });
          stopPlanPolling();
          // No activation here: the agent goes onto the site only from the
          // "Add to my site" click (Webflow #1197336).
          setStep(addStepIndex + 2);
        }
      } catch (e) {
        setPlanCheckError(t('onboard_checking_subscription'));
      }
    }, 2000);
  }

  // What Design's CTA leads to: the Test step when it is shown, else the add step.
  const designCtaLabel = withTest ? t('onboard_test_chatbot_btn') : t('continue');

  // Adding the agent to the site is its own step (Webflow #1197336): what
  // happens and on which site, the one button that adds it, then the publish
  // prompt. The CTA below leaves for the portal whether or not it was added.
  const addToSiteSlide = (
    <div className="card-button-wrapper">
      <div className="card wf-add-step" role="group" aria-label={t('wf_add_to_site_title', { siteName: siteHost })}>
        <div className="header-back-button">
          <BackButton step={step} setStep={setStep} />
          <h2>{t('wf_add_step_title')}</h2>
        </div>
        <p className="sub">{t('wf_add_to_site_text')}</p>
        {siteHost && <p className="wf-add-step__site">{siteHost}</p>}
        {addedToSite ? (
          <>
            <p className="wf-add-step__done" role="status">
              <FontAwesomeIcon icon={faCircleCheck} /> {t('wf_add_to_site_done', { siteName: siteHost })}
            </p>
            {(needsPublish || publishedSite) && (
              <WebflowPublishNotice
                botId={bot}
                siteId={siteId}
                siteName={siteHost}
                onPublished={() => setPublishedSite(true)}
              />
            )}
          </>
        ) : (
          <>
            <button type="button" className="wf-add-step__btn" onClick={addToSite} disabled={adding || !bot || !siteId}>
              <FontAwesomeIcon icon={adding ? faSpinner : faPlus} spin={adding} />
              {t('wf_add_to_site_button')}
            </button>
            {addError && <p className="err">{t('wf_add_to_site_failed')}</p>}
          </>
        )}
      </div>
      <button
        className="btn"
        disabled={loading}
        onClick={() => {
          if (SHOW_SUBSCRIPTION_STEP) {
            setStep(addStepIndex + 1);
            return;
          }
          handlePortalForward(true, token, uid);
        }}
      >
        <div className="portal-button-content">
          <FontAwesomeIcon icon={faCircleRight} />
          {SHOW_SUBSCRIPTION_STEP ? t('start_free_trial') : t('onboard_access_portal_btn')}
        </div>
      </button>
    </div>
  );

  const allSlides = [
    /* step 0 – register / login toggle */
    (
      authMode === 'register'
        ? (
          <div className="card-button-wrapper">
            <form className="card" onSubmit={handlePw}>
              <h2>{t('onboard_welcome')}</h2>
              <p className="sub">
                <Trans i18nKey="onboard_register_subtitle" components={{ 1: <strong /> }} />
              </p>

              <input
                className="register-input-text"
                type="text"
                placeholder={t('first_name') || 'First name'}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />

              <input
                className="register-input-text"
                type="email"
                placeholder={t('email')}
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                required
              />

              <input
                className="register-input-text"
                type="password"
                placeholder={t('password')}
                value={pw}
                onChange={e => setPw(e.target.value)}
                required
              />

              <input
                className="register-input-text"
                type="password"
                placeholder={t('confirm_password')}
                value={pw2}
                onChange={e => setPw2(e.target.value)}
                required
              />

              {err && <div className="err">{err}</div>}

              <p className="link" style={{ marginTop: 12 }}>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => switchAuthMode('login', { keepEmail: true })}
                >
                  {t('already_have_account')}
                </span>
              </p>
            </form>

            <button
              className="btn"
              type="button"
              onClick={handlePw}
              disabled={loading}
            >
              {t('get_started')} {loading && <span className="spin" />}
            </button>
          </div>
        )
        : (
          <div className="card-button-wrapper">
            <form className="card" onSubmit={handleLogin}>
              <h2>{t('onboard_welcome_back')}</h2>
              <p className="sub">
                <Trans i18nKey="onboard_login_subtitle" components={{ 1: <strong /> }} />
              </p>

              <input
                className="register-input-text"
                type="email"
                placeholder={t('email')}
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                required
              />

              {codeSent ? (
                <input
                  className="register-input-text"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder={t('wf_code_placeholder')}
                  aria-label={t('wf_code_placeholder')}
                  value={signinCode}
                  onChange={e => setSigninCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
              ) : (
                <input
                  className="register-input-text"
                  type="password"
                  placeholder={t('password')}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  required
                />
              )}

              {notice && !err && <div className="wf-notice" role="status">{notice}</div>}
              {err && <div className="err">{err}</div>}

              <p className="link" style={{ marginTop: 12 }}>
                <button type="button" className="wf-link" onClick={sendCode} disabled={loading}>
                  {codeSent ? t('wf_resend_code') : t('wf_send_code')}
                </button>
                {codeSent && (
                  <>
                    {' · '}
                    <button type="button" className="wf-link" onClick={() => { setCodeSent(false); setSigninCode(''); setNotice(''); setErr(''); }}>
                      {t('wf_use_password')}
                    </button>
                  </>
                )}
              </p>
              <p className="link" style={{ marginTop: 6 }}>
                <button type="button" className="wf-link" onClick={() => switchAuthMode('register')}>
                  {t('wf_use_other_email')}
                </button>
              </p>
            </form>

            <button
              className="btn"
              type="button"
              onClick={handleLogin}
              disabled={loading || (codeSent ? signinCode.trim().length !== 6 : !pw)}
            >
              {codeSent ? t('wf_sign_in_with_code') : (t('login') || 'Log in')} {loading && <span className="spin" />}
            </button>
          </div>
        )
    ),

    /* step 2 – url selection */
    <div className='card-button-wrapper'>
      <form className={`card${isTraining ? ' building' : ''}`} onSubmit={e=>e.preventDefault()}>
        <h2>{t('onboard_create_chatbot_title')}</h2>
        <p className="sub">
          <Trans i18nKey="onboard_url_step_subtitle_webflow" components={{ 1: <br /> }} />
        </p>
        <select
            className={`select${err ? ' input-error' : ''}`}
            value={selectedUrl}
            onChange={e => {
              const val = e.target.value;
              setSelectedUrl(val);
              const match = urls.find(u => u.url === val);
              setSiteId(match ? match.site_id : '');
            }}
            disabled={loading || isTraining}
            required
          >
            <option value="" disabled>{t('onboard_select_site_url_option')}</option>
            {urls.map((u, i) => (
              <option key={i} value={u.url}>{u.url}</option>
            ))}
          </select>
        {!pageLoading && token && urls.length === 0 && (
          <div className="wf-notice" role="status">
            <strong>{t('wf_no_sites_title')}</strong>{' '}
            {t('wf_no_sites_text')}{' '}
            <button type="button" className="wf-link" onClick={chooseSiteAgain}>{t('wf_no_sites_button')}</button>
          </div>
        )}

        {isTraining && (
          <div className='chatbot-loading-container'>
            <Atom color="#5616ea" size="medium" speedPlus={'0'} />
            {(() => {
              const rounded = Math.round(pct);
              let label;

              // Nothing is fetched from the Webflow site on this flow.
              label = rounded <= 5 ? t("training_starting") : t("training_finalizing");

              return `${label}: ${rounded}%`;
            })()}
          </div>
        )}
        {slowBuild && isTraining && (
          <div className="wf-notice" role="status">
            {t('wf_build_slow')}{' '}
            <button type="button" className="wf-link" onClick={() => handlePortalForward(true, token, uid)}>
              {t('wf_build_slow_continue')}
            </button>
          </div>
        )}
        {err && <div className="err" style={{marginTop: '0px'}}>{err}</div>}
        <div className="privacy-note">
          <br />{t('onboard_privacy_note')}
        </div>
      </form>
      <button className="btn" type="button" onClick={createBot} disabled={loading || isTraining}>
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : t('onboard_create_chatbot_btn')}
      </button>
    </div>,

    /* step 3 – customise */
    <div className='card-button-wrapper'>
      <div className='preview-parent-container'>
        <div className='preview-header-text'>
          <div className='header-back-button'>
            <h2>{t('onboard_customize_ai_assistant_title')}</h2>
          </div>
          <p className="sub" style={{marginBottom: "0px"}}>
            {t('onboard_customize_desc_long')}
          </p>
        </div>
        <div className="preview-card">
          <div className='user-settings'>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title" htmlFor="themeColorPicker">{t('theme_color')}</label>
                <input
                  id="themeColorPicker"
                  type="color"
                  value={widgetStyles.themeColor}
                  onChange={(e) => handleStyleChange("themeColor", e.target.value)}
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t('header_font_color')}</label>
                <input
                  type="color"
                  value={widgetStyles.headerColor}
                  onChange={(e) =>
                    handleStyleChange("headerColor", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t('button_hover_color')}</label>
                <input
                  type="color"
                  value={widgetStyles.hoverColor}
                  onChange={(e) => handleStyleChange("hoverColor", e.target.value)}
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t("enable_pulsing")}</label>
                <div className="radio-buttons">
                  <div className="radio-buttons-content">
                    <input
                      type="radio"
                      id="pulsing-enabled"
                      name="pulsing"
                      value="enabled"
                      checked={widgetStyles.pulsing === true}
                      onChange={() => handleStyleChange("pulsing", true)}
                    />
                    <label htmlFor="pulsing-enabled">{t("enabled")}</label>
                  </div>
                  <div className="radio-buttons-content">
                    <input
                      type="radio"
                      id="pulsing-disabled"
                      name="pulsing"
                      value="disabled"
                      checked={widgetStyles.pulsing === false}
                      onChange={() => handleStyleChange("pulsing", false)}
                    />
                    <label htmlFor="pulsing-disabled">{t("disabled")}</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="control-group">
              <label>
                <div className="border-radius-container">
                  <div className="widget-setting-title">{t('widget_size')}</div>
                  <div>
                    <input
                      type="range"
                      min="40"
                      max="100"
                      value={widgetStyles.widgetSize}
                      onChange={(e) =>
                        handleStyleChange("widgetSize", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div className="divider"></div>
          <div className="preview-area">
            <div
              className={`chat-widget-icon
                          ${widgetStyles.pulsing ? "pulsing" : ""}
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "align-left" : "align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "elevated" : "bottom"}`}
              style={{
                width:  `${widgetStyles.widgetSize}px`,
                height: `${widgetStyles.widgetSize}px`,
                borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                backgroundColor: widgetStyles.icon ? "transparent" : widgetStyles.themeColor,
                flex: "0 0 auto",
                display: "flex",
              }}
            >
              {widgetStyles.icon ? (
                <img
                  src={widgetStyles.icon}
                  alt="Widget Icon"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                  }}
                />
              ) :
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent", paddingTop: "3px"}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="65%" viewBox="0 0 24 24" fill="white" className="size-6">
                    <path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" />
                  </svg>
                </div>
              }
            </div>

            <div
              className={`chat-window show
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "align-left" : "align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "elevated" : "bottom"}`}
              style={{
                width: "280px",
                height: "380px",
              }}
            >
              <div
                className="chat-header"
                style={{ backgroundColor: widgetStyles.themeColor }}
              >
                <div className="chat-header-title">
                  <div className="logo-message-container">
                    {widgetStyles.logo && (
                      <img
                        src={widgetStyles.logo}
                        alt="Chat Logo"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <span
                      className="onboarding-chat-title"
                      style={{ color: widgetStyles.headerColor }}
                    >
                      {widgetStyles.headerText || t('chat_with_us')}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="onboarding-widget-close-button"
                    style={{ color: widgetStyles.headerColor }}
                    aria-label={t('aria_close_chat')}
                  >
                    <FontAwesomeIcon icon={faX}/>
                  </button>
                </div>
              </div>

              <div className="chat-body">
                <div className="widget-message widget-bot-message">
                  {widgetStyles.welcomeMessage || t('onboard_preview_welcome_long')}
                </div>
              </div>

              <div className="chat-footer">
                <div className="powered-by">
                  <span className="powered-by-text">{t('powered_by_ultimo_bots')}</span>
                </div>

                <div className="input-send-container">
                  <input
                    type="text"
                    className="chat-input"
                    placeholder=""
                    disabled
                  />
                  <button
                    type="button"
                    className="onboarding-send-message"
                    style={{
                      backgroundColor: widgetStyles.themeColor,
                      color: widgetStyles.headerColor,
                    }}
                  >
                    <i className="widget-send-icon"><FontAwesomeIcon icon={faArrowUp}/></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .onboarding-send-message:hover {
              background-color: ${widgetStyles.hoverColor} !important;
            }
            .preview-area .loading-dots div {
              background-color: ${widgetStyles.themeColor} !important;
            }
          `}</style>
        </div>
        {err && <div className="err">{err}</div>}
      </div>
      <button className="btn" disabled={loading} onClick={() => saveWidgetStyles()}>
        <div className='portal-button-content'>
          <FontAwesomeIcon icon={faCircleRight} />
          {designCtaLabel} {loading && <span className="spin" />}
        </div>
      </button>
    </div>,

    ...(!withTest ? [] : [
    /* step 4 – test */
    <div className='card-button-wrapper'>
      <div className="chatbot-card">
        <div className='test-assistant-text-container'>
          <div className='header-back-button'>
            <BackButton step={step} setStep={setStep} />
            <h2>{t('onboard_test_chatbot_title')}</h2>
          </div>
          <p className="sub" style={{marginBottom: '10px'}}>
            {t("test_assistant_desc")}
          </p>
        </div>
        {step >= 2 && (
          <BotChatStep
            bot={bot}
          />
        )}
      </div>
      <button className="btn" disabled={loading} onClick={() => setStep(4)}>
        <div className="portal-button-content">
          <FontAwesomeIcon icon={faCircleRight} />
          {t('continue')}
        </div>
      </button>
    </div>,
    ]),

    /* add to the Webflow site: its own step after Test (or Design) */
    addToSiteSlide,

    /* step 5 – choose subscription */
    <div className="subscription-section">
      <div className="header-back-button">
        {isCheckingPlan ? (
          <button className="lp-back-btn" type="button" onClick={() => stopPlanPolling()}>
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginLeft: "1px" }} />
          </button>
        ) : (
          <BackButton step={step} setStep={setStep} />
        )}
        <h2>{t('onboard_trial_headline')}</h2>
      </div>

      <p className="sub" style={{ marginBottom: '20px' }}>
        <Trans i18nKey="onboard_trial_subtitle" components={{ 1: <br />, 2: <strong /> }} />
      </p>

      {/* Either loader OR pricing UI */}
      {isCheckingPlan ? (
        /* ===== Loader mode ===== */
        <div className="plan-wait-card" style={{ margin: '0 auto 16px', maxWidth: 520 }}>
          <Atom color="#5616ea" size="medium" speedPlus={'0'} />
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <div style={{ fontWeight: 600 }}>{t('onboard_plan_waiting')}</div>
            <div style={{ opacity: 0.8, marginTop: 6 }}>
              {t('onboard_plan_waiting_hint')}
            </div>
            {planCheckError && (
              <div className="err" style={{ marginTop: 10 }}>{planCheckError}</div>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  try {
                    const name = await fetchBotPlanName();
                    if (name !== "Free") {
                      stopPlanPolling();
                      setStep(addStepIndex + 2);
                    }
                  } catch {
                    setPlanCheckError(t('onboard_still_checking_subscription'));
                  }
                }}
              >
                {t('onboard_refresh_now')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ===== Pricing UI mode ===== */
        <>
          {/* Monthly / Yearly toggle */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 0 }}>
            <BillingCycleToggle
              billingCycle={billingCycle}
              setBillingCycle={setBillingCycle}
              t={t}
            />
          </div>

          <div className="plan-slider-wrapper" ref={planWrapperRef}>
            <div className="plan-slider">
              {/* SMART */}
              <div
                className="plan-card"
                ref={(el) => (planCardRefs.current[0] = el)}
              >
                <h3 style={{ margin: '0' }}>{t("plan_smart")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="yearly-crossout">
                        <span className="wix-plan-price">
                          {t(priceMap.YEAR.smart.yearlyDiscountedKey)}
                        </span>
                        <span className="old-price">{t(priceMap.YEAR.smart.yearlyOriginalKey)}</span>
                        <span className="deal-tag">{t(priceMap.YEAR.smart.savePct)}</span>
                      </div>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wix-plan-price">{t(priceMap.MONTH.smart.labelKey)}</p>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_smart_responses')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_smart_knowledge')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_smart_skills')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_smart_behaviors')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_auto_lead_capture')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_connect')}
                    <span className="icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="feature-platform-icon" />
                      <span className="icon-tooltip facebook">{t('platform_facebook_messenger')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="feature-platform-icon" />
                      <span className="icon-tooltip instagram">{t('platform_instagram_dms')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="feature-platform-icon" />
                      <span className="icon-tooltip whatsapp">WhatsApp</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="feature-platform-icon" />
                      <span className="icon-tooltip google-drive">Google Drive</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="feature-platform-icon" />
                      <span className="icon-tooltip onedrive">{t('platform_microsoft_onedrive')}</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faXmark} className="plan-icons-not-included" />
                    {t('plan_feat_auto_rescraping')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faXmark} className="plan-icons-not-included" />
                    {t('plan_feat_remove_branding')}
                  </li>
                </ul>
                <button
                  className="wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("smart")}
                >
                  {t("start_free_trial")} {loading && <span className="spin" />}
                </button>
              </div>

              {/* BOOST */}
              <div
                className={`plan-card ${activePlanIdx === 1 ? "highlighted" : ""}`}
                ref={(el) => (planCardRefs.current[1] = el)}
              >
                <span className="plan-badge">{t("recommended_start")}</span>
                <h3 style={{ margin: '0' }}>{t("plan_boost")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="yearly-crossout">
                        <span className="wix-plan-price">
                          {t(priceMap.YEAR.boost.yearlyDiscountedKey)}
                        </span>
                        <span className="old-price">{t(priceMap.YEAR.boost.yearlyOriginalKey)}</span>
                        <span className="deal-tag">{t(priceMap.YEAR.boost.savePct)}</span>
                      </div>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wix-plan-price">{t(priceMap.MONTH.boost.labelKey)}</p>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_boost_responses')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_boost_knowledge')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_boost_skills')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_boost_behaviors')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_auto_lead_capture')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_connect')}
                    <span className="icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="feature-platform-icon" />
                      <span className="icon-tooltip facebook">{t('platform_facebook_messenger')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="feature-platform-icon" />
                      <span className="icon-tooltip instagram">{t('platform_instagram_dms')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="feature-platform-icon" />
                      <span className="icon-tooltip whatsapp">WhatsApp</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="feature-platform-icon" />
                      <span className="icon-tooltip google-drive">Google Drive</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="feature-platform-icon" />
                      <span className="icon-tooltip onedrive">{t('platform_microsoft_onedrive')}</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_rescraping_monthly')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_remove_branding')}
                  </li>
                </ul>
                <button
                  className="wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("boost")}
                >
                  {t("start_free_trial")} {loading && <span className="spin" />}
                </button>
              </div>

              {/* ULTIMO */}
              <div
                className={`plan-card ${activePlanIdx === 2 ? "highlighted" : ""}`}
                ref={(el) => (planCardRefs.current[2] = el)}
              >
                <h3 style={{ margin: '0' }}>{t("plan_ultimo")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="yearly-crossout">
                        <span className="wix-plan-price">
                          {t(priceMap.YEAR.ultimo.yearlyDiscountedKey)}
                        </span>
                        <span className="old-price">{t(priceMap.YEAR.ultimo.yearlyOriginalKey)}</span>
                        <span className="deal-tag">{t(priceMap.YEAR.ultimo.savePct)}</span>
                      </div>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wix-plan-price">{t(priceMap.MONTH.ultimo.labelKey)}</p>
                      <p className="wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_ultimo_responses')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_ultimo_knowledge')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_ultimo_skills')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_ultimo_behaviors')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_auto_lead_capture')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_connect')}
                    <span className="icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="feature-platform-icon" />
                      <span className="icon-tooltip facebook">{t('platform_facebook_messenger')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="feature-platform-icon" />
                      <span className="icon-tooltip instagram">{t('platform_instagram_dms')}</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="feature-platform-icon" />
                      <span className="icon-tooltip whatsapp">WhatsApp</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="feature-platform-icon" />
                      <span className="icon-tooltip google-drive">Google Drive</span>
                    </span>
                    <span className="icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="feature-platform-icon" />
                      <span className="icon-tooltip onedrive">{t('platform_microsoft_onedrive')}</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_rescraping_daily')}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="plan-icons-included" />
                    {t('plan_feat_remove_branding')}
                  </li>
                </ul>
                <button
                  className="wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("ultimo")}
                >
                  {t("start_free_trial")} {loading && <span className="spin" />}
                </button>
              </div>
            </div>
          </div>

          <div className="wf-pricing-indicators">
            {plans.map((_, i) => (
              <div
                key={i}
                className={`wf-indicator ${i === activePlanIdx ? "active" : ""}`}
                onClick={() => {
                  setActivePlanIdx(i);
                  const card = planCardRefs.current[i];
                  if (card) card.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                }}
              />
            ))}
          </div>

          <p className="cancel-note">{t("cancel_note")}</p>

          <p className="free-plan-link">
            <button
              type="button"
              className="free-plan-link-button"
              onClick={() => { handlePortalForward(true, token, uid); }}
            >
              {t('onboard_continue_free_plan')}
            </button>
          </p>

        </>
      )}
    </div>,

    /* step 6 – success & next steps */
    <div className="card">
      <h2 className='complete-title'>
        <Trans i18nKey="onboard_chatbot_live_title" components={{ 1: <span className='live-highlight' /> }} />
      </h2>

      <div className="after-setup-links">
        <div className='integration-complete-content'>
          <FontAwesomeIcon icon={faPenToSquare} className='chatbot-integrated-icon'/>
          {t('onboard_customize_test_chatbot')}
        </div>
        <div className="centered-button-wrapper">
          <button
            className="integration-button-link"
            onClick={() => { handlePortalForward(true, token, uid); }}
          >
            <div className='portal-button-content'>
              <FontAwesomeIcon icon={faCircleRight} className='forward-portal-icon'/>
              <>{t('onboard_portal_btn')}</>
            </div>
          </button>
        </div>
      </div>
    </div>
  ];

  // Hidden, not deleted: drop the trailing paid + success slides so the wizard
  // ends at the add step. The visible indices are unchanged, so every setStep
  // call above still points at the same slide, and the step counter reads
  // 5 of 5 instead of 5 of 7.
  const slides = SHOW_SUBSCRIPTION_STEP
    ? allSlides
    : allSlides.slice(0, addStepIndex + 1);

  if (pageLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (callbackError) {
    return (
      <div className="wrapper register-scope">
        <img className="logo-register-site" src="/ultimo-bots-white.png" alt="logo" />
        <div className="card-button-wrapper" data-testid="wf-callback-error">
          <div className="card">
            <h2>{t('wf_oauth_error_title')}</h2>
            <p className="sub">{callbackError}</p>
          </div>
          <button
            className="btn"
            type="button"
            onClick={() => {
              callbackExchange = null;
              window.location.assign(`${process.env.REACT_APP_API_BASE_URL}auth/webflow/connect`);
            }}
          >
            {t('wf_oauth_try_again')}
          </button>
          {localStorage.getItem('token') && (
            // Signed-in customers (e.g. "Connect Webflow" from the dashboard)
            // get a way back without starting a new install.
            <p className="link" style={{ marginTop: 16 }}>
              <span
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { callbackExchange = null; if (onConnected) onConnected('/overview'); else navigate('/overview', { replace: true }); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
              >
                {t('wf_back_to_dashboard')}
              </span>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper register-scope">
      <img className="logo-register-site" src="/ultimo-bots-white.png" alt="logo" />
      <div className="step-ind">{t('onboard_step_counter', { current: step + 1, total: slides.length })}</div>
      <div className="steps-wrapper">
        <div className="steps-slider" style={{transform:`translateX(-${step*100}%)`}}>
          {slides.map((c,i)=>(
            <div key={i} className="step-slide">{c}</div>
          ))}
        </div>
      </div>


    </div>
  );
}
